import React from 'react';
import { FileText, Users, Mail, ShieldCheck, Send, Scale, Globe, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import vivoLogo from '../assets/vivosign-logo.png';
import { useTheme } from '../contexts/ThemeContext';

const FEATURES = [
  { icon: FileText, titleKey: 'features.templates', descKey: 'features.templates_desc' },
  { icon: Users, titleKey: 'features.recipients', descKey: 'features.recipients_desc' },
  { icon: Mail, titleKey: 'features.notifications', descKey: 'features.notifications_desc' },
  { icon: ShieldCheck, titleKey: 'features.audit', descKey: 'features.audit_desc' },
] as const;

interface AuthLayoutProps {
  children: React.ReactNode;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const { t, i18n } = useTranslation('auth');
  const { theme, toggleTheme } = useTheme();
  const currentLang = i18n.language?.substring(0, 2) || 'en';
  const toggleLang = () => i18n.changeLanguage(currentLang === 'en' ? 'fr' : 'en');

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left panel — feature showcase */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] shrink-0 flex-col justify-between text-white p-10 xl:p-14" style={{ backgroundColor: '#0b1120' }}>
        <div>
          {/* Brand hero */}
          <div className="flex justify-center mb-10">
            <a href="https://vivosign.com">
              <img src={vivoLogo} alt="VivoSign" className="w-full max-w-[320px] hover:opacity-90 transition-opacity" />
            </a>
          </div>

          {/* Headline */}
          <h1 className="text-3xl xl:text-4xl font-bold leading-tight mb-4">
            {t('features.headline')}
          </h1>
          <p className="text-slate-400 text-base mb-8">
            {t('features.subtitle')}
          </p>

          {/* Compliance badge */}
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-10">
            <Scale className="h-4 w-4 shrink-0" />
            <span>{t('features.compliance')}</span>
          </div>

          {/* Quick Send — featured first */}
          <div className="flex items-center gap-3 mb-8">
            <div className="shrink-0 rounded-md bg-slate-800 p-2">
              <Send className="h-4 w-4 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{t('features.quick_send')}</p>
              <p className="text-slate-400 text-sm">{t('features.quick_send_desc')}</p>
            </div>
            <a
              href={currentLang === 'fr' ? '/bonjour' : '/hi'}
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
            >
              <Send className="h-4 w-4" />
              {t('features.try_now')}
            </a>
          </div>

          {/* Feature list */}
          <div className="space-y-6">
            {FEATURES.map(({ icon: Icon, titleKey, descKey }) => (
              <div key={titleKey} className="flex items-start gap-3">
                <div className="mt-0.5 rounded-md bg-slate-800 p-2">
                  <Icon className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <p className="font-semibold text-sm">{t(titleKey)}</p>
                  <p className="text-slate-400 text-sm">{t(descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tagline */}
        <p className="text-emerald-400 font-semibold mt-10">
          {t('features.tagline')}
        </p>
      </div>

      {/* Right panel — form */}
      <div className="relative flex-1 flex items-center justify-center bg-slate-100 dark:bg-slate-950 p-4 sm:p-8">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border bg-white dark:bg-slate-800 text-foreground hover:bg-muted transition-colors shadow-sm"
            title={theme === 'dark' ? t('features.light_mode', 'Light mode') : t('features.dark_mode', 'Dark mode')}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={toggleLang}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-white dark:bg-slate-800 text-sm font-medium text-foreground hover:bg-muted transition-colors shadow-sm"
          >
            <Globe className="h-4 w-4" />
            {currentLang === 'en' ? 'FR' : 'EN'}
          </button>
        </div>
        <div className="w-full max-w-lg">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
